import { BOARD_SIZE, SQUARE_COUNT } from '../constants';
import { BASE_ABILITIES } from './abilities';
import { homeRank, pawnStartRank, squareIndex } from './squares';
import type { Board, Owner, Piece, PieceKind, Side } from './types';

export const createPiece = (id: number, owner: Owner, kind: PieceKind, home: number): Piece => ({
  id,
  owner,
  kind,
  abilities: BASE_ABILITIES[kind],
  gained: [],
  learnedFrom: {},
  home,
  lives: 1,
  royal: kind === 'king',
  moved: false,
  stoppedTurns: 0,
  uncapturableTurns: 0,
  knightTurns: 0,
  passThroughTurns: 0,
  backwardTurns: 0,
  randomMoveTurns: 0,
  explodeIn: 0,
  dormantTurns: 0,
  cloneIn: 0,
  randomNextMove: false,
  shortSlides: false,
  movesTwice: false,
  capturesAgain: false,
  killsCaptor: false,
  demotesCaptor: false,
  stealsAbility: false,
  passesAbilities: false,
  mustCapture: false,
  pushes: false,
  heldBy: null,
  casterId: null,
  energy: 0,
  energyCap: 0,
  pitch: null,
});

const BACK_RANK_ORDER: readonly PieceKind[] = [
  'rook',
  'knight',
  'bishop',
  'queen',
  'king',
  'bishop',
  'knight',
  'rook',
];

export const createInitialBoard = (): { board: Board; nextPieceId: number } => {
  const board: (Piece | null)[] = Array.from({ length: SQUARE_COUNT }, () => null);
  let nextPieceId = 1;

  const place = (side: Side): void => {
    for (let file = 0; file < BOARD_SIZE; file += 1) {
      const kind = BACK_RANK_ORDER[file] as PieceKind;
      const back = squareIndex(file, homeRank(side));
      const front = squareIndex(file, pawnStartRank(side));
      board[back] = createPiece(nextPieceId++, side, kind, back);
      board[front] = createPiece(nextPieceId++, side, 'pawn', front);
    }
  };

  place('gold');
  place('violet');
  return { board, nextPieceId };
};

export const withPiece = (board: Board, square: number, piece: Piece | null): Board => {
  const next = [...board];
  next[square] = piece;
  return next;
};

export const findPieceSquare = (board: Board, pieceId: number): number | null => {
  const index = board.findIndex((piece) => piece?.id === pieceId);
  return index === -1 ? null : index;
};

export const squaresOf = (board: Board, owner: Owner): number[] => {
  const result: number[] = [];
  board.forEach((piece, square) => {
    if (piece?.owner === owner) result.push(square);
  });
  return result;
};

export const emptySquares = (board: Board): number[] => {
  const result: number[] = [];
  board.forEach((piece, square) => {
    if (piece === null) result.push(square);
  });
  return result;
};

export const otherSide = (side: Side): Side => (side === 'gold' ? 'violet' : 'gold');

export const isSide = (owner: Owner): owner is Side => owner !== 'neutral';
